import { colors, glassCard, typography, radius, cardBlur, btnPrimary } from '../../theme';
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    collection, getDocs, addDoc, getDoc,
    doc, query, where, Timestamp, serverTimestamp, updateDoc, arrayUnion
} from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { Box, Typography, CircularProgress, Button, TextField } from '@mui/material';
import { CheckCircle, Star } from '@mui/icons-material';
import logo from '../../images/logo.svg';
import barberFallback from '../../images/barber-fallback.png';

const DAY_KEYS = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
const SPECIFIC_BARBER_SURCHARGE = 5;

function getEffectiveHours(barber, shop) {
    const dayKey = DAY_KEYS[new Date().getDay()];
    return barber?.operatingHours?.[dayKey] ?? shop?.operatingHours?.[dayKey] ?? null;
}

function isBarberAvailableToday(barber, shop) {
    if (barber?.isAvailable === false) return false;
    const hours = getEffectiveHours(barber, shop);
    return !!hours && !hours.isOff;
}

function generateSlots(openTime = '09:00', closeTime = '18:00', duration = 30) {
    const slots = [];
    const [oh, om] = openTime.split(':').map(Number);
    const [ch, cm] = closeTime.split(':').map(Number);
    let cur = oh * 60 + om;
    const end = ch * 60 + cm;
    while (cur + duration <= end) {
        const h = Math.floor(cur / 60), m = cur % 60;
        slots.push({ label: `${h % 12 === 0 ? 12 : h % 12}:${m.toString().padStart(2,'0')} ${h < 12 ? 'AM' : 'PM'}`, minutes: cur });
        cur += duration;
    }
    return slots;
}

function minutesToDate(date, minutes) {
    const d = new Date(date);
    d.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
    return d;
}

function timeAgo(isoString) {
    if (!isoString) return '';
    const diff = Date.now() - new Date(isoString).getTime();
    const mins = Math.floor(diff / 60000), hours = Math.floor(mins / 60), days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (mins > 0) return `${mins}m ago`;
    return 'just now';
}

const isSubscriptionActive = (shop) => {
    const plan = shop?.plan ?? 'free';
    if (plan === 'free') return true;
    const paidUntil = shop?.paidUntil?.toDate?.() ?? (shop?.paidUntil ? new Date(shop.paidUntil) : null);
    if (!paidUntil) return false;
    return paidUntil > new Date();
};

const getNextAvailableSlot = async (shopId, barber, shop) => {
    const hours = getEffectiveHours(barber, shop);
    if (!hours || hours.isOff) return null;
    const now = new Date();
    const today = new Date(); today.setHours(0,0,0,0);
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    const allSlots = generateSlots(hours.open, hours.close, 30);
    const snap = await getDocs(query(collection(db,'bookings'), where('barberId','==',barber.id), where('scheduledAt','>=',Timestamp.fromDate(today)), where('scheduledAt','<',Timestamp.fromDate(tomorrow)), where('status','!=','cancelled')));
    const bookedMinutes = snap.docs.map(d => d.data().slotMinutes);
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    if (bookedMinutes.length === 0) {
        const cur = allSlots.find((s, i) => { const next = allSlots[i+1]?.minutes ?? (s.minutes+30); return s.minutes <= nowMinutes && nowMinutes < next; });
        if (cur) return { ...cur, isNow: true };
    }
    return allSlots.find(s => !bookedMinutes.includes(s.minutes) && s.minutes > nowMinutes) || null;
};

// ─── Stars ────────────────────────────────────────────────────────────────────
const Stars = ({ rating, size = 12 }) => (
    <Box sx={{ display: 'flex', gap: 0.2 }}>
        {[1,2,3,4,5].map(s => (
            <Star key={s} sx={{ fontSize: size, color: s <= Math.round(rating ?? 0) ? colors.primary : colors.border }} />
        ))}
    </Box>
);

// ─── Photo lightbox ───────────────────────────────────────────────────────────
const PhotoLightbox = ({ item, onClose }) => (
    <Box onClick={onClose} sx={{ position: 'fixed', inset: 0, zIndex: 300, bgcolor: 'rgba(0,0,0,0.92)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 2 }}>
        <Box component="img" src={item.url} alt={item.caption} sx={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 2, objectFit: 'contain' }} />
        {item.caption && <Typography sx={{ fontSize: 14, color: '#fff', fontWeight: 600, mt: 2, textAlign: 'center' }}>{item.caption}</Typography>}
        <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', mt: 1 }}>Tap anywhere to close</Typography>
    </Box>
);

// ─── Reviews Section ──────────────────────────────────────────────────────────
const ReviewsSection = ({ reviews = [], rating, ratingCount }) => {
    const scrollRef = useRef(null);
    const timerRef  = useRef(null);
    const pauseRef  = useRef(false);

    const validReviews = reviews.filter(r => r.rating > 0 || r.review).sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt));
    const avg = rating ?? 0;

    useEffect(() => {
        if (validReviews.length <= 1) return;
        const el = scrollRef.current; if (!el) return;
        let pos = 0;
        const tick = () => { if (!pauseRef.current && el) { pos += 0.5; if (pos >= el.scrollWidth / 2) pos = 0; el.scrollLeft = pos; } timerRef.current = requestAnimationFrame(tick); };
        timerRef.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(timerRef.current);
    }, [validReviews.length]);

    if (validReviews.length === 0 && avg <= 0) return null;
    const displayed = validReviews.length > 1 ? [...validReviews, ...validReviews] : validReviews;

    return (
        <Box sx={{ pt: 2.5, pb: 1.5, bgcolor: colors.bg }}>
            <Box sx={{ px: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Box>
                    <Typography sx={{ fontFamily: typography.poppins, fontSize: 15, fontWeight: 700, color: colors.textPrimary }}>What customers say</Typography>
                    {avg > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.3 }}>
                            <Stars rating={avg} size={11} />
                            <Typography sx={{ fontSize: 11, color: colors.textFaint }}>{avg.toFixed(1)} · {ratingCount ?? 0} review{(ratingCount ?? 0) !== 1 ? 's' : ''}</Typography>
                        </Box>
                    )}
                </Box>
                {avg > 0 && (
                    <Box sx={{ ...glassCard, borderRadius: 2, px: 1.5, py: 0.8, textAlign: 'center' }}>
                        <Typography sx={{ fontFamily: typography.poppins, fontSize: 22, fontWeight: 700, color: colors.primary, lineHeight: 1 }}>{avg.toFixed(1)}</Typography>
                        <Typography sx={{ fontSize: 9, color: colors.textFaint, mt: 0.2 }}>out of 5</Typography>
                    </Box>
                )}
            </Box>
            <Box ref={scrollRef} onMouseEnter={() => { pauseRef.current = true; }} onMouseLeave={() => { pauseRef.current = false; }} onTouchStart={() => { pauseRef.current = true; }} onTouchEnd={() => { pauseRef.current = false; }}
                sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', px: 3, pb: 1, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' }, cursor: 'grab' }}>
                {displayed.map((r, i) => (
                    <Box key={`${r.bookingId ?? i}-${i}`} sx={{ flexShrink: 0, width: 220, ...glassCard, borderRadius: 2.5, p: 2, position: 'relative', overflow: 'hidden' }}>
                        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, bgcolor: colors.primary }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            {r.rating > 0 ? <Stars rating={r.rating} size={11} /> : <Box />}
                            <Typography sx={{ fontSize: 9, color: colors.textDisabled }}>{timeAgo(r.createdAt)}</Typography>
                        </Box>
                        {r.review ? (
                            <Typography sx={{ fontSize: 12, color: colors.textMuted, lineHeight: 1.6, mb: 1.2, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>"{r.review}"</Typography>
                        ) : (
                            <Typography sx={{ fontSize: 12, color: colors.textFaintest, lineHeight: 1.6, mb: 1.2, fontStyle: 'italic' }}>No comment left</Typography>
                        )}
                        {r.services?.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                                {r.services.slice(0,2).map((svc, j) => (
                                    <Box key={j} sx={{ px: 0.8, py: 0.2, bgcolor: colors.primaryDim, border: `1px solid ${colors.primaryBorder}`, borderRadius: 1 }}>
                                        <Typography sx={{ fontSize: 9, color: colors.primary }}>✂️ {svc}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                            <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: colors.primaryDim, border: `1px solid ${colors.primaryBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <Typography sx={{ fontSize: 10 }}>👤</Typography>
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 600, color: colors.textPrimary, lineHeight: 1.2 }} noWrap>{r.customerName ?? 'Walk-in'}</Typography>
                                <Typography sx={{ fontSize: 9, color: colors.textFaint }} noWrap>with {r.barberName}</Typography>
                            </Box>
                        </Box>
                    </Box>
                ))}
            </Box>
            {validReviews.length > 1 && <Typography sx={{ fontSize: 10, color: colors.textFaintest, textAlign: 'center', mt: 0.5 }}>← swipe →</Typography>}
        </Box>
    );
};

// ─── Barber Choice Step ───────────────────────────────────────────────────────
const BarberChoiceStep = ({ onChoose }) => (
    <Box sx={{ minHeight: '100vh', bgcolor: colors.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 3 }}>
        <img src={logo} alt="DigitalQ" style={{ width: 160, height: 'auto', marginBottom: 32 }} />
        <Typography sx={{ fontFamily: typography.poppins, fontSize: 22, fontWeight: 700, color: colors.textPrimary, textAlign: 'center', mb: 1 }}>
            How do you want your barber?
        </Typography>
        <Typography sx={{ fontSize: 13, color: colors.textFaint, textAlign: 'center', mb: 4 }}>Pick your preference to get started</Typography>

        <Box onClick={() => onChoose('any')} sx={{ width: '100%', maxWidth: 360, ...glassCard, borderRadius: 3, p: 2.5, mb: 2, cursor: 'pointer', transition: 'all .2s', '&:hover': { transform: 'translateY(-2px)' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: colors.primaryDim, border: `1px solid ${colors.primaryBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>⚡</Box>
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary, mb: 0.3 }}>Next Available Barber</Typography>
                    <Typography sx={{ fontSize: 12, color: colors.textFaint }}>Shortest wait, served immediately</Typography>
                </Box>
                <Box sx={{ px: 1.5, py: 0.4, borderRadius: radius.md, bgcolor: colors.successBg, border: `1px solid ${colors.successBorder}` }}>
                    <Typography sx={{ fontSize: 11, color: colors.success, fontWeight: 700 }}>FREE</Typography>
                </Box>
            </Box>
        </Box>

        <Box onClick={() => onChoose('specific')} sx={{ width: '100%', maxWidth: 360, ...glassCard, borderRadius: 3, p: 2.5, cursor: 'pointer', transition: 'all .2s', position: 'relative', overflow: 'hidden', '&:hover': { transform: 'translateY(-2px)' } }}>
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: colors.primary }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: colors.primaryDim, border: `1px solid ${colors.primaryBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>✂️</Box>
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: colors.primary, mb: 0.3 }}>Choose My Barber</Typography>
                    <Typography sx={{ fontSize: 12, color: colors.textFaint }}>Pick who cuts your hair</Typography>
                </Box>
                <Box sx={{ px: 1.5, py: 0.4, borderRadius: radius.md, bgcolor: colors.warningBg, border: `1px solid ${colors.warningBorder}` }}>
                    <Typography sx={{ fontSize: 11, color: colors.warning, fontWeight: 700 }}>+RM5</Typography>
                </Box>
            </Box>
        </Box>
        <Typography sx={{ fontSize: 11, color: colors.textFaintest, mt: 3, textAlign: 'center' }}>RM5 surcharge applies when selecting a specific barber</Typography>
    </Box>
);

// ─── shared TextField sx ──────────────────────────────────────────────────────
const fieldSx = {
    '& .MuiOutlinedInput-root': {
        bgcolor: colors.primaryDim, borderRadius: 2, color: colors.textPrimary, fontSize: 15,
        '& fieldset': { borderColor: colors.primaryBorder },
        '&:hover fieldset': { borderColor: colors.primary },
        '&.Mui-focused fieldset': { borderColor: colors.primary },
    },
    '& input': { color: colors.textPrimary, letterSpacing: 0.3 },
    '& input::placeholder': { color: colors.textFaintest, opacity: 1 },
};

// ─── Phone Step ───────────────────────────────────────────────────────────────
const PhoneStep = ({ shopName, barberName, slotLabel, queueNumber, surcharge, haircutPrice, onConfirm, loading }) => {
    const [name,       setName]       = useState('');
    const [phone,      setPhone]      = useState('');
    const [nameError,  setNameError]  = useState('');
    const [phoneError, setPhoneError] = useState('');
    const totalPrice = (haircutPrice ?? 0) + surcharge;

    const handleConfirm = () => {
        let valid = true;
        if (!name.trim()) { setNameError('Please enter your name'); valid = false; }
        const digits = phone.replace(/\D/g, '');
        if (digits.length === 0) { setPhoneError('Please enter your phone number'); valid = false; }
        else if (digits.length < 9) { setPhoneError('Please enter a valid Malaysian phone number'); valid = false; }
        if (!valid) return;
        setNameError(''); setPhoneError('');
        onConfirm(digits, name.trim());
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: colors.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 3, py: 4 }}>
            <img src={logo} alt="DigitalQ" style={{ width: 120, height: 'auto', marginBottom: 24 }} />

            {/* Summary card */}
            <Box sx={{ ...glassCard, borderRadius: 3, p: 2.5, mb: 4, width: '100%', maxWidth: 360, textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: colors.primary }} />
                <Typography sx={{ fontSize: 12, color: colors.textFaint, mb: 0.5 }}>You're joining</Typography>
                <Typography sx={{ fontFamily: typography.poppins, fontSize: 18, fontWeight: 700, color: colors.textPrimary, mb: 0.3 }}>{barberName}</Typography>
                <Typography sx={{ fontSize: 12, color: colors.textFaint, mb: 2 }}>{shopName}</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mb: 2 }}>
                    <Box>
                        <Typography sx={{ fontSize: 10, color: colors.textFaint }}>Queue No.</Typography>
                        <Typography sx={{ fontFamily: typography.poppins, fontSize: 28, fontWeight: 700, color: colors.primary, lineHeight: 1 }}>#{queueNumber}</Typography>
                    </Box>
                    <Box sx={{ width: 1, bgcolor: colors.border }} />
                    <Box>
                        <Typography sx={{ fontSize: 10, color: colors.textFaint }}>Time Slot</Typography>
                        <Typography sx={{ fontFamily: typography.poppins, fontSize: 22, fontWeight: 700, color: colors.primary, lineHeight: 1.2 }}>{slotLabel}</Typography>
                    </Box>
                </Box>
                {totalPrice > 0 && (
                    <Box sx={{ borderTop: `1px solid ${colors.border}`, pt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {haircutPrice != null && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography sx={{ fontSize: 12, color: colors.textFaint }}>✂️ Haircut</Typography>
                                <Typography sx={{ fontSize: 12, color: colors.textMuted }}>RM{haircutPrice}</Typography>
                            </Box>
                        )}
                        {surcharge > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography sx={{ fontSize: 12, color: colors.textFaint }}>⭐ Barber selection fee</Typography>
                                <Typography sx={{ fontSize: 12, color: colors.textMuted }}>+RM{surcharge}</Typography>
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5, pt: 0.5, borderTop: `1px dashed ${colors.border}` }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.textPrimary }}>Estimated Total</Typography>
                            <Typography sx={{ fontFamily: typography.poppins, fontSize: 18, fontWeight: 700, color: colors.primary }}>RM{totalPrice}</Typography>
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Inputs */}
            <Box sx={{ width: '100%', maxWidth: 360 }}>
                <Typography sx={{ fontFamily: typography.poppins, fontSize: 18, fontWeight: 700, color: colors.textPrimary, mb: 0.5 }}>
                    📱 Get notified automatically
                </Typography>
                <Typography sx={{ fontSize: 13, color: colors.textFaint, mb: 2.5, lineHeight: 1.6 }}>
                    Enter your details and we'll send you a WhatsApp when it's your turn.
                </Typography>

                {/* Name field */}
                <Typography sx={{ fontSize: 12, color: colors.textMuted, mb: 0.8, fontWeight: 500 }}>Your name</Typography>
                <TextField
                    fullWidth size="small"
                    placeholder="e.g. Ahmad bin Razif"
                    value={name}
                    onChange={e => { setName(e.target.value); setNameError(''); }}
                    error={!!nameError}
                    inputProps={{ maxLength: 60 }}
                    sx={{ ...fieldSx, mb: nameError ? 0.5 : 2 }}
                />
                {nameError && <Typography sx={{ fontSize: 11, color: colors.error, mb: 1.5 }}>{nameError}</Typography>}

                {/* Phone field */}
                <Typography sx={{ fontSize: 12, color: colors.textMuted, mb: 0.8, fontWeight: 500 }}>WhatsApp number</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: phoneError ? 0.5 : 1 }}>
                    <Box sx={{ bgcolor: colors.primaryDim, border: `1px solid ${phoneError ? colors.error : colors.primaryBorder}`, borderRadius: 2, px: 1.5, display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        <Typography sx={{ fontSize: 14 }}>🇲🇾</Typography>
                        <Typography sx={{ fontSize: 13, color: colors.textMuted }}>+60</Typography>
                    </Box>
                    <TextField
                        fullWidth size="small"
                        placeholder="11-1234 5678"
                        value={phone}
                        onChange={e => { setPhone(e.target.value.replace(/\D/g,'')); setPhoneError(''); }}
                        type="tel"
                        inputProps={{ maxLength: 11 }}
                        error={!!phoneError}
                        sx={{
                            '& .MuiOutlinedInput-root': { bgcolor: colors.primaryDim, borderRadius: 2, color: colors.textPrimary, fontSize: 15, '& fieldset': { borderColor: phoneError ? colors.error : colors.primaryBorder }, '&:hover fieldset': { borderColor: colors.primary }, '&.Mui-focused fieldset': { borderColor: colors.primary } },
                            '& input': { color: colors.textPrimary, letterSpacing: 1 },
                            '& input::placeholder': { color: colors.textFaintest, opacity: 1 },
                        }}
                    />
                </Box>
                {phoneError && <Typography sx={{ fontSize: 11, color: colors.error, mb: 1 }}>{phoneError}</Typography>}
                <Typography sx={{ fontSize: 11, color: colors.textFaint, mb: 3 }}>Format: 011-1234 5678 or 012-345 6789</Typography>

                <Button fullWidth variant="contained" onClick={handleConfirm} disabled={loading}
                    sx={{ ...btnPrimary, py: 1.3, fontSize: 14, mb: 1.5 }}>
                    {loading ? <CircularProgress size={18} sx={{ color: colors.bg }} /> : '🔔 Notify me when I\'m next'}
                </Button>
            </Box>
        </Box>
    );
};

// ─── Main WalkInPage ──────────────────────────────────────────────────────────
const WalkInPage = () => {
    const { shopId } = useParams();
    const navigate   = useNavigate();

    const [shop,            setShop]            = useState(null);
    const [barbers,         setBarbers]         = useState([]);
    const [loading,         setLoading]         = useState(true);
    const [submitting,      setSubmitting]      = useState(false);
    const [selectedBarber,  setSelectedBarber]  = useState(null);
    const [nextSlot,        setNextSlot]        = useState(null);
    const [slotLoading,     setSlotLoading]     = useState(false);
    const [error,           setError]           = useState('');
    const [todayQueueCount, setTodayQueueCount] = useState(0);
    const [step,            setStep]            = useState('choice');
    const [barberMode,      setBarberMode]      = useState(null);
    const [pendingBooking,  setPendingBooking]  = useState(null);
    const [lightboxItem,    setLightboxItem]    = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const today = new Date(); today.setHours(0,0,0,0);
                const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
                const [shopSnap, barbersSnap] = await Promise.all([getDoc(doc(db,'shops',shopId)), getDocs(collection(db,'shops',shopId,'barbers'))]);
                const shopData = shopSnap.exists() ? { id: shopSnap.id, ...shopSnap.data() } : null;
                if (shopData) setShop(shopData);
                const barbersWithCount = await Promise.all(barbersSnap.docs.map(async (d) => {
                    const b = { id: d.id, ...d.data() };
                    if (b.isAvailable === false) return null;
                    if (!isBarberAvailableToday(b, shopData)) b.isOffToday = true;
                    try {
                        const qSnap = await getDocs(query(collection(db,'bookings'), where('shopId','==',shopId), where('type','==','walkin'), where('barberId','==',d.id), where('createdAt','>=',Timestamp.fromDate(today)), where('createdAt','<',Timestamp.fromDate(tomorrow))));
                        b.todayQueueCount = qSnap.size;
                    } catch { b.todayQueueCount = 0; }
                    return b;
                }));
                setBarbers(barbersWithCount.filter(Boolean));
            } catch (e) { setError('Failed to load shop info.'); }
            finally { setLoading(false); }
        };
        load();
    }, [shopId]);

    useEffect(() => {
        if (barberMode !== 'any' || barbers.length === 0 || !shop) return;
        const available = barbers.filter(b => !b.isOffToday);
        const pool = available.length > 0 ? available : barbers;
        setSelectedBarber([...pool].sort((a,b) => (a.todayQueueCount??0) - (b.todayQueueCount??0))[0]);
    }, [barberMode, barbers, shop]);

    useEffect(() => {
        if (!selectedBarber || !shop) return;
        const fetchSlot = async () => {
            setSlotLoading(true); setNextSlot(null);
            const today = new Date(); today.setHours(0,0,0,0);
            const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
            try {
                const qSnap = await getDocs(query(collection(db,'bookings'), where('shopId','==',shopId), where('type','==','walkin'), where('barberId','==',selectedBarber.id), where('createdAt','>=',Timestamp.fromDate(today)), where('createdAt','<',Timestamp.fromDate(tomorrow))));
                setTodayQueueCount(qSnap.size);
            } catch { setTodayQueueCount(0); }
            setNextSlot(await getNextAvailableSlot(shopId, selectedBarber, shop));
            setSlotLoading(false);
        };
        fetchSlot();
    }, [selectedBarber, shop]);

    const handleChoiceSelect = (mode) => { setBarberMode(mode); setStep('select'); };

    const handleProceed = () => {
        if (!selectedBarber) { setError('Please select a barber.'); return; }
        if (!nextSlot) { setError('No available slots for this barber today.'); return; }
        setError('');
        const surcharge  = barberMode === 'specific' ? SPECIFIC_BARBER_SURCHARGE : 0;
        const services   = shop?.services ?? [];
        const haircutSvc = services.find(s => s.name?.toLowerCase() === 'haircut') ?? services[0] ?? null;
        setPendingBooking({ barberName: selectedBarber.name, barberId: selectedBarber.id, shopName: shop?.shopName, slotLabel: nextSlot.label, queueNumber: todayQueueCount+1, surcharge, haircutSvc, haircutPrice: haircutSvc?.price ?? null });
        setStep('phone');
    };

    // ── now accepts customerName from PhoneStep ──
    const handleConfirmWithPhone = async (phone, customerName) => {
        setSubmitting(true);
        try {
            const today = new Date(); today.setHours(0,0,0,0);
            const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
            const qSnap = await getDocs(query(collection(db,'bookings'), where('shopId','==',shopId), where('type','==','walkin'), where('barberId','==',selectedBarber.id), where('createdAt','>=',Timestamp.fromDate(today)), where('createdAt','<',Timestamp.fromDate(tomorrow))));
            const queueNumber  = qSnap.size + 1;
            const surcharge    = barberMode === 'specific' ? SPECIFIC_BARBER_SURCHARGE : 0;
            const haircutSvc   = pendingBooking?.haircutSvc ?? null;
            const haircutPrice = haircutSvc?.price ?? null;
            const totalPrice   = (haircutPrice ?? 0) + surcharge;
            // use the entered name instead of the generic "Walk-in #N"
            const displayName  = customerName || `Walk-in #${queueNumber}`;
            const bookingRef   = await addDoc(collection(db,'bookings'), {
                type: 'walkin', shopId, barberId: selectedBarber.id, barberName: selectedBarber.name,
                shopName: shop?.shopName, userId: auth.currentUser?.uid ?? null,
                customerName: displayName,
                customerPhone: phone ? `60${phone.startsWith('0') ? phone.slice(1) : phone}` : null,
                queueNumber, service: haircutSvc?.name ?? null, servicePrice: haircutPrice,
                serviceDuration: haircutSvc?.duration ?? null, surcharge,
                totalPrice: totalPrice > 0 ? totalPrice : null,
                slotMinutes: nextSlot.minutes,
                scheduledAt: Timestamp.fromDate(minutesToDate(new Date(), nextSlot.minutes)),
                status: 'waiting', paymentStatus: 'unpaid', barberMode, notes: '',
                date: new Date().toISOString().split('T')[0], createdAt: serverTimestamp(),
            });
            await updateDoc(doc(db,'shops',shopId,'barbers',selectedBarber.id), { waitingList: arrayUnion(bookingRef.id) });
            navigate(`/queue/${shopId}/${bookingRef.id}`);
        } catch (e) { console.error(e); setError('Failed to join queue. Please try again.'); setStep('select'); setSubmitting(false); }
    };

    const estimatedQueueNumber = todayQueueCount + 1;
    const surcharge       = barberMode === 'specific' ? SPECIFIC_BARBER_SURCHARGE : 0;
    const shopRating      = shop?.rating ?? shop?.ratings?.average ?? 0;
    const shopRatingCount = shop?.ratingCount ?? shop?.ratings?.totalReviews ?? 0;

    if (loading) return (
        <Box sx={{ minHeight: '100vh', bgcolor: colors.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress sx={{ color: colors.primary }} />
        </Box>
    );

    if (shop && !isSubscriptionActive(shop)) return (
        <Box sx={{ minHeight: '100vh', bgcolor: colors.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 3, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 36, mb: 2 }}>🔒</Typography>
            <Typography sx={{ fontFamily: typography.poppins, fontSize: 20, fontWeight: 700, color: colors.textPrimary, mb: 1 }}>Shop Unavailable</Typography>
            <Typography sx={{ fontSize: 13, color: colors.textFaint, maxWidth: 280 }}>This barbershop is currently not accepting walk-ins.</Typography>
        </Box>
    );

    if (step === 'choice') return <BarberChoiceStep onChoose={handleChoiceSelect} />;
    if (step === 'phone' && pendingBooking) return (
        <PhoneStep
            shopName={pendingBooking.shopName}
            barberName={pendingBooking.barberName}
            slotLabel={pendingBooking.slotLabel}
            queueNumber={pendingBooking.queueNumber}
            surcharge={pendingBooking.surcharge}
            haircutPrice={pendingBooking.haircutPrice}
            loading={submitting}
            onConfirm={handleConfirmWithPhone}
        />
    );

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: colors.bg, color: colors.textPrimary, pb: 24 }}>

            {lightboxItem && <PhotoLightbox item={lightboxItem} onClose={() => setLightboxItem(null)} />}

            {/* ── Shop Hero ── */}
            <Box sx={{ position: 'relative', height: 220, width: '100%', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', inset: 0, backgroundImage: `url(${shop?.image || '/default-shop.jpg'})`, backgroundSize: 'cover', backgroundPosition: 'center', transform: 'scale(1.05)' }} />
                <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.80) 100%)' }} />
                <Box sx={{ position: 'absolute', bottom: 14, left: 14, right: 14, backdropFilter: 'blur(14px)', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 3, px: 2.5, py: 1.8, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography sx={{ fontFamily: typography.poppins, fontSize: 20, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>{shop?.shopName ?? 'Walk In'}</Typography>
                    {shop?.address && <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.75)' }}>📍 {shop.address}</Typography>}
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Box sx={{ px: 2, py: 0.5, borderRadius: 999, backdropFilter: 'blur(10px)', background: 'rgba(34,197,94,0.18)', border: '1px solid rgba(34,197,94,0.45)', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: colors.success, boxShadow: `0 0 8px ${colors.success}` }} />
                            <Typography sx={{ fontSize: 12, color: colors.success, fontWeight: 600 }}>Walk-in Queue Open</Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* ── Reviews ── */}
            <ReviewsSection reviews={shop?.reviews ?? []} rating={shopRating} ratingCount={shopRatingCount} />

            <Box sx={{ mx: 3, height: '1px', bgcolor: colors.border }} />

            {/* ── Barber section header ── */}
            <Box sx={{ px: 3, pt: 2.5, pb: 1.5, bgcolor: colors.bg }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography sx={{ fontFamily: typography.poppins, fontSize: 16, fontWeight: 600, color: colors.textPrimary }}>
                            {barberMode === 'any' ? 'Next Available Barber' : 'Choose a Barber'}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: colors.textFaint, mt: 0.2 }}>
                            {barberMode === 'any' ? 'Auto-assigned for shortest wait' : 'Pick who cuts your hair (+RM5)'}
                        </Typography>
                    </Box>
                    <Button onClick={() => { setStep('choice'); setSelectedBarber(null); setNextSlot(null); setBarberMode(null); }}
                        sx={{ color: colors.textFaintest, fontSize: 11, textTransform: 'none', minWidth: 0, px: 1 }}>
                        ← Change
                    </Button>
                </Box>
                {barberMode === 'specific' && (
                    <Box sx={{ mt: 1.5, px: 1.5, py: 1, bgcolor: colors.warningBg, border: `1px solid ${colors.warningBorder}`, borderRadius: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: 13 }}>💰</Typography>
                        <Typography sx={{ fontSize: 12, color: colors.warning }}>+RM5 barber selection fee will be added to your bill</Typography>
                    </Box>
                )}
            </Box>

            {/* ── Barber cards ── */}
            <Box sx={{ px: 3, display: 'flex', flexDirection: 'column', gap: 1.5, bgcolor: colors.bg }}>
                {barbers.map((barber) => {
                    const active         = selectedBarber?.id === barber.id;
                    const isAutoSelected = barberMode === 'any' && active;
                    const bRating        = barber.rating ?? barber.ratings?.average ?? 0;
                    const bRatingCount   = barber.ratingCount ?? barber.ratings?.totalReviews ?? 0;
                    const isOffToday     = barber.isOffToday === true;

                    return (
                        <Box key={barber.id}
                            onClick={() => { if (isOffToday) return; if (barberMode === 'specific') { setSelectedBarber(barber); setError(''); setTodayQueueCount(0); } }}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 2,
                                background:     active ? colors.primaryDim : colors.bgCard,
                                backdropFilter: active ? 'none' : cardBlur,
                                WebkitBackdropFilter: active ? 'none' : cardBlur,
                                border:    `1px solid ${active ? colors.primary : colors.border}`,
                                boxShadow: active ? `0 0 0 2px ${colors.primary}40` : `0 2px 12px ${colors.shadow}`,
                                borderRadius: 2.5, p: 1.5,
                                cursor: isOffToday ? 'not-allowed' : (barberMode === 'specific' ? 'pointer' : 'default'),
                                transition: 'all .2s',
                                '&:hover': (!isOffToday && barberMode === 'specific') ? { borderColor: colors.primaryBorder } : {},
                                opacity: isOffToday ? 0.4 : (isAutoSelected ? 1 : (barberMode === 'any' ? 0.45 : 1)),
                                position: 'relative', overflow: 'hidden',
                            }}>
                            {active && <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, bgcolor: active ? '#32292060' : colors.primary, borderRadius: '3px 0 0 3px' }} />}

                            <Box sx={{ position: 'relative', flexShrink: 0 }}>
                                <Box component="img" src={barber.image || barberFallback} alt={barber.name}
                                    sx={{ width: 54, height: 54, borderRadius: '50%', objectFit: 'cover', border: active ? '2px solid rgba(50,41,32,0.3)' : `2px solid ${colors.border}` }} />
                                {isOffToday && (
                                    <Box sx={{ position: 'absolute', bottom: -2, left: '50%', transform: 'translateX(-50%)', bgcolor: colors.errorBg, border: `1px solid ${colors.errorBorder}`, borderRadius: 0.5, px: 0.5, py: 0.1, whiteSpace: 'nowrap' }}>
                                        <Typography sx={{ fontSize: 7, color: colors.error, fontWeight: 700 }}>OFF TODAY</Typography>
                                    </Box>
                                )}
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.2 }}>
                                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: isOffToday ? colors.textFaintest : (active ? '#322920' : colors.textPrimary) }}>{barber.name}</Typography>
                                    {isAutoSelected && (
                                        <Box sx={{ px: 0.8, py: 0.1, bgcolor: colors.successBg, border: `1px solid ${colors.successBorder}`, borderRadius: 0.5 }}>
                                            <Typography sx={{ fontSize: 9, color: colors.success, fontWeight: 700 }}>AUTO</Typography>
                                        </Box>
                                    )}
                                    {isOffToday && (
                                        <Box sx={{ px: 0.8, py: 0.1, bgcolor: colors.errorBg, border: `1px solid ${colors.errorBorder}`, borderRadius: 0.5 }}>
                                            <Typography sx={{ fontSize: 9, color: colors.error, fontWeight: 700 }}>NOT AVAILABLE</Typography>
                                        </Box>
                                    )}
                                </Box>
                                <Typography sx={{ fontSize: 11, color: active ? '#32292088' : colors.textFaint, mb: 0.3 }}>
                                    {barber.specialty?.slice(0,2).join(' · ') || 'General cuts'}
                                </Typography>
                                {bRating > 0 && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Stars rating={bRating} size={10} />
                                        <Typography sx={{ fontSize: 10, color: active ? '#32292099' : colors.textFaint }}>
                                            {bRating.toFixed(1)}{bRatingCount > 0 && <Typography component="span" sx={{ fontSize: 9 }}> ({bRatingCount})</Typography>}
                                        </Typography>
                                    </Box>
                                )}
                                {barber.portfolio?.length > 0 && (
                                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, overflowX: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
                                        {barber.portfolio.slice(0,6).map((item, i) => (
                                            <Box key={i} onClick={e => { e.stopPropagation(); setLightboxItem(item); }}
                                                sx={{ flexShrink: 0, width: 34, height: 34, borderRadius: 1, overflow: 'hidden', border: `1px solid ${colors.border}`, cursor: 'pointer' }}>
                                                <Box component="img" src={item.url} alt={item.caption} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                            </Box>
                                        ))}
                                        {barber.portfolio.length > 6 && (
                                            <Box sx={{ flexShrink: 0, width: 34, height: 34, borderRadius: 1, bgcolor: colors.bgDeep, border: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <Typography sx={{ fontSize: 8, color: colors.textFaintest }}>+{barber.portfolio.length - 6}</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </Box>

                            <Box sx={{ textAlign: 'center', minWidth: 44, flexShrink: 0 }}>
                                {isOffToday ? (
                                    <Typography sx={{ fontSize: 9, color: colors.textFaintest, textTransform: 'uppercase' }}>Off</Typography>
                                ) : (
                                    <>
                                        <Typography sx={{ fontFamily: typography.poppins, fontSize: 22, fontWeight: 700, color: active ? '#322920' : colors.textFaintest, lineHeight: 1 }}>
                                            {barber.todayQueueCount ?? 0}
                                        </Typography>
                                        <Typography sx={{ fontSize: 9, color: active ? '#32292066' : colors.textDisabled, textTransform: 'uppercase', letterSpacing: 0.5 }}>waiting</Typography>
                                    </>
                                )}
                            </Box>

                            {active && <CheckCircle sx={{ fontSize: 18, color: '#322920', flexShrink: 0 }} />}
                        </Box>
                    );
                })}
            </Box>

            {/* ── Slot preview ── */}
            {selectedBarber && (
                <Box sx={{ px: 3, mt: 2.5, bgcolor: colors.bg, pb: 2 }}>
                    <Box sx={{ ...glassCard, borderRadius: 2.5, p: 2 }}>
                        <Typography sx={{ fontSize: 11, color: colors.textFaint, mb: 1.5 }}>
                            Next available slot for <Typography component="span" sx={{ color: colors.primary, fontWeight: 600, fontSize: 11 }}>{selectedBarber.name}</Typography>
                        </Typography>
                        {slotLoading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={14} sx={{ color: colors.primary }} />
                                <Typography sx={{ fontSize: 12, color: colors.textFaint }}>Checking availability...</Typography>
                            </Box>
                        ) : nextSlot ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography sx={{ fontFamily: typography.poppins, fontSize: 28, fontWeight: 700, color: colors.primary, lineHeight: 1 }}>{nextSlot.label}</Typography>
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.8, px: 1, py: 0.3, borderRadius: 1, bgcolor: nextSlot.isNow ? colors.tealBg : colors.successBg, border: `1px solid ${nextSlot.isNow ? colors.tealBorder : colors.successBorder}` }}>
                                        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: nextSlot.isNow ? colors.teal : colors.success }} />
                                        <Typography sx={{ fontSize: 10, color: nextSlot.isNow ? colors.teal : colors.success }}>{nextSlot.isNow ? 'Available Now' : 'Available'}</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ width: 68, height: 68, borderRadius: '50%', border: `2px solid ${colors.primaryBorder}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: colors.primaryDim }}>
                                    <Typography sx={{ fontSize: 9, color: colors.textFaint, lineHeight: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>Queue</Typography>
                                    <Typography sx={{ fontFamily: typography.poppins, fontSize: 24, fontWeight: 700, color: colors.primary, lineHeight: 1.2 }}>#{estimatedQueueNumber}</Typography>
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{ bgcolor: colors.errorBg, border: `1px solid ${colors.errorBorder}`, borderRadius: 2, p: 1.5 }}>
                                <Typography sx={{ fontSize: 13, color: colors.error, fontWeight: 600, mb: 0.3 }}>
                                    ❌ {selectedBarber.isOffToday ? 'Not working today' : 'No slots available today'}
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: colors.textFaint }}>
                                    {selectedBarber.isOffToday ? `${selectedBarber.name} is off today.` : `All slots for ${selectedBarber.name} are full.`}
                                </Typography>
                            </Box>
                        )}

                        {selectedBarber?.portfolio?.length > 0 && (
                            <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${colors.border}` }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: colors.textMuted }}>{selectedBarber.name}'s work</Typography>
                                    <Typography sx={{ fontSize: 10, color: colors.textFaintest }}>{selectedBarber.portfolio.length} photo{selectedBarber.portfolio.length !== 1 ? 's' : ''}</Typography>
                                </Box>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.8 }}>
                                    {selectedBarber.portfolio.map((item, i) => (
                                        <Box key={i} onClick={() => setLightboxItem(item)} sx={{ position: 'relative', borderRadius: 1.5, overflow: 'hidden', aspectRatio: '1', border: `1px solid ${colors.border}`, cursor: 'pointer' }}>
                                            <Box component="img" src={item.url} alt={item.caption} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                            {item.caption && (
                                                <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, transparent 100%)', px: 0.8, py: 0.6 }}>
                                                    <Typography sx={{ fontSize: 9, color: '#fff', fontWeight: 600 }} noWrap>{item.caption}</Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                                <Typography sx={{ fontSize: 10, color: colors.textFaintest, mt: 1, textAlign: 'center' }}>Tap any photo to enlarge</Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            )}

            {error && <Box sx={{ px: 3, mt: 2 }}><Typography sx={{ fontSize: 13, color: colors.error, textAlign: 'center' }}>{error}</Typography></Box>}

            {/* ── Fixed bottom bar ── */}
            <Box sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: colors.bgCard, backdropFilter: cardBlur, WebkitBackdropFilter: cardBlur, borderTop: `1px solid ${colors.border}`, px: 3, py: 2 }}>
                {selectedBarber && nextSlot && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.2 }}>
                        <Typography sx={{ fontSize: 12, color: colors.textFaint }}>
                            {selectedBarber.name} · <Typography component="span" sx={{ color: colors.primary, fontSize: 12, fontWeight: 600 }}>{nextSlot.isNow ? 'Now' : nextSlot.label}</Typography>
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {surcharge > 0 && (
                                <Box sx={{ px: 1, py: 0.3, borderRadius: radius.sm, bgcolor: colors.warningBg, border: `1px solid ${colors.warningBorder}` }}>
                                    <Typography sx={{ fontSize: 11, color: colors.warning }}>+RM{surcharge}</Typography>
                                </Box>
                            )}
                            <Box sx={{ px: 1.5, py: 0.3, borderRadius: radius.sm, bgcolor: colors.primaryDim, border: `1px solid ${colors.primaryBorder}` }}>
                                <Typography sx={{ fontSize: 12, color: colors.primary, fontWeight: 700 }}>#{estimatedQueueNumber}</Typography>
                            </Box>
                        </Box>
                    </Box>
                )}
                <Button fullWidth variant="contained" onClick={handleProceed}
                    disabled={!selectedBarber || !nextSlot || slotLoading}
                    sx={{ ...btnPrimary, fontSize: 15, py: 1.5 }}>
                    ✂️ Confirm Walk-in
                </Button>
            </Box>
        </Box>
    );
};

export default WalkInPage;